import { useEffect, useState } from 'preact/hooks';
import { ErrorBox, Loading } from '../components/ui';
import { api, type Card } from '../lib/api';
import { diffSnapshots } from '../lib/diff';
import { Link, navigate } from '../lib/router';
import { errorText, toast, useAsync } from '../lib/store';

interface Rev {
  version: number;
  change_note: string | null;
  actor: string;
  created_at: string;
  snapshot?: any;
}

const who = (a: string) => (a === 'user' ? 'You' : a.startsWith('client:') ? a.slice(7).replace(/^chatgpt$/, 'ChatGPT') : a);

export function History({ id }: { id: string }) {
  const card = useAsync(() => api.get<Card>(`/cards/${id}`), [id]);
  const revs = useAsync(() => api.get<{ items: Rev[] }>(`/cards/${id}/revisions`), [id]);
  const [sel, setSel] = useState<number | null>(null);
  const [pair, setPair] = useState<{ cur: Rev; prev: Rev | null } | null>(null);

  useEffect(() => {
    if (revs.data && sel === null) setSel(revs.data.items[0]?.version ?? null);
  }, [revs.data]);

  useEffect(() => {
    if (sel === null) return;
    let alive = true;
    (async () => {
      const cur = await api.get<Rev>(`/cards/${id}/revisions/${sel}`);
      const prevMeta = revs.data?.items.find((r) => r.version < sel);
      const prev = prevMeta ? await api.get<Rev>(`/cards/${id}/revisions/${prevMeta.version}`) : null;
      if (alive) setPair({ cur, prev });
    })().catch((e) => toast(errorText(e), 'error'));
    return () => {
      alive = false;
    };
  }, [sel]);

  if (card.error || revs.error) return <ErrorBox error={card.error ?? revs.error} />;
  if (!card.data || !revs.data) return <Loading />;
  const c = card.data;

  const restore = async (v: number) => {
    if (!confirm(`Restore version ${v}? This creates a new version; nothing is lost.`)) return;
    try {
      await api.post(`/cards/${id}/revisions/${v}/restore`, {}, { 'if-match': `"${c.version}"` });
      toast(`Restored version ${v}`);
      navigate(`/c/${c.id}/${c.slug}`);
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const d = pair ? diffSnapshots(pair.prev?.snapshot ?? {}, pair.cur.snapshot) : null;

  return (
    <>
      <p>
        <Link href={`/c/${c.id}/${c.slug}`}>← {c.title}</Link>
      </p>
      <h1>History</h1>
      <div class="split">
        <ul class="rev-list" aria-label="Revisions">
          {revs.data.items.map((r) => (
            <li>
              <button aria-current={r.version === sel} onClick={() => setSel(r.version)}>
                <div class="row-between">
                  <strong>v{r.version}</strong>
                  <span class="small muted">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <div class="small">
                  <span class={r.actor === 'user' ? '' : 'badge badge-review'}>{who(r.actor)}</span> {r.change_note && <span class="muted">— {r.change_note}</span>}
                </div>
              </button>
            </li>
          ))}
        </ul>
        <section aria-live="polite">
          {!pair || !d ? (
            <Loading />
          ) : (
            <>
              <div class="row-between">
                <h2>
                  v{pair.cur.version} {pair.prev ? `vs v${pair.prev.version}` : '(created)'}
                </h2>
                {pair.cur.version !== c.version && (
                  <button class="dr-btn" onClick={() => restore(pair.cur.version)}>
                    Restore this version
                  </button>
                )}
              </div>
              {!d.fields.length && !d.ingredients.length && !d.steps.length && <p class="muted">No content changes.</p>}
              {(d.fields.length > 0 || d.steps.length > 0) && (
                <table class="diff">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Before</th>
                      <th>After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...d.fields, ...d.steps].map((f) => (
                      <tr>
                        <td>{f.field}</td>
                        <td class="before">{f.before}</td>
                        <td class="after">{f.after}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {d.ingredients.length > 0 && (
                <>
                  <h3 style={{ marginTop: 'var(--bk-space-5)' }}>Ingredients</h3>
                  <table class="diff">
                    <tbody>
                      {d.ingredients.map((x) => (
                        <tr>
                          <td>{x.kind}</td>
                          <td class={x.before ? 'before' : ''}>{x.before ?? ''}</td>
                          <td class={x.after ? 'after' : ''}>{x.after ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
}
